// @ts-nocheck
import { JxsComponent } from '@jxs/component';
import { formatMoney } from '@jxs/money-format';
import { replaceNodeText, copyToClipboard } from '@jxs/utilities';

/**
 * JxsSectionSettings - Section 配置桥接组件。
 *
 * 该组件通常由 `_jxs-section-settings.liquid` 输出到页面中，本身不负责展示内容，
 * 只负责读取 Liquid 渲染出的 data-* 配置，并把这些配置转换成实际作用在
 * `.jxs-section-body` / `.shopify-section` 上的 class 或 DOM 结构。
 *
 * 当前主要处理：
 * - 移动端/桌面端 section 宽度模式（全宽、页面宽度）；
 * - 将背景图元素移动到 Shopify section 根节点前方，方便统一定位和层级控制。
 */
class JxsSectionSettings extends JxsComponent {
  constructor() {
    super();
    const section = this.closest('.shopify-section');
    // 配置组件只作为数据载体存在，不参与页面视觉展示。
    this.style.display = 'none'
    const sectionBody = this.closest('.jxs-section')?.querySelector('.jxs-section-body');
    if (sectionBody) {
      // 如果 Liquid 或其他脚本已经设置过宽度 class，则不重复覆盖，避免产生冲突。
      if (sectionBody.classList.contains('jxs-section--full-width') || sectionBody.classList.contains('jxs-section--page-width')) {
        return;
      }

      // 移动端宽度：默认按页面宽度处理，data-page-width-mobile="full" 时切换为全宽。
      if (this.dataset.pageWidthMobile == 'full') {
        sectionBody.classList.add('jxs-section--full-width');
      } else {
        sectionBody.classList.add('jxs-section--page-width');
      }

      // 桌面端宽度：使用独立 class，便于 CSS 在不同断点下覆盖移动端规则。
      if (this.dataset.pageWidthDesktop == 'full') {
        sectionBody.classList.add('jxs-section--desktop-full-width');
      } else {
        sectionBody.classList.add('jxs-section--desktop-page-width');
      }
    }

    const backgroundImage = this.querySelector('.jxs-section-background-image')
    if (backgroundImage) {
      if (section) {
        // 背景图脱离配置组件，提升到 section 根节点，避免被内容容器布局影响。
        section.prepend(backgroundImage);
      }
    }

    if (!this.getDataset('maxWidthDisabled')) {
      section?.classList.add('jxs-section--max-width-enabled');
    }
  }
}
customElements.define('jxs-section-settings', JxsSectionSettings);

/**
 * JxsMultiSpanText - 多段文本样式标记组件。
 *
 * 用于把富文本中的简写标记 `[序号 文本]` 转换成可单独设置样式的 span。
 * 搭配内部由 Liquid 输出的 `style[sn][block-id]`，可以让不同序号的文字片段
 * 绑定到对应 block 的样式 class，从而实现同一段标题/文案中局部变色、加粗等效果。
 *
 * 示例：
 * 输入：`新品 [1 限时折扣] 今日开启`
 * 输出：`新品 <span class="text-span" sn="1">限时折扣</span> 今日开启`
 */
class JxsMultiSpanText extends JxsComponent {
  constructor() {
    super();
  }

  /**
   * 元素挂载后解析文本标记并应用对应样式。
   *
   * 解析和样式绑定放在 connectedCallback 中执行，是为了确保组件内部的
   * 文本节点、style 标签和 Liquid 输出内容都已经进入 DOM。
   */
  connectedCallback() {
    super.connectedCallback()
    this.parseSpanMarkers();
    this.applySpanStyles();
  }

  /**
   * 解析 [n 文字] 格式，转换为 <span class="text-span" sn="n">文字</span>
   */
  parseSpanMarkers() {
    const pattern = /\[(\d+)\s+([^\]]+)\]/g;
    this.innerHTML = this.innerHTML.replace(pattern, '<span class="text-span" sn="$1">$2</span>');
  }

  /**
   * 找到所有 text-span 子块的 style，并为对应 sn 的 span 设置 id
   * 使 #block-id 选择器能匹配上
   */
  applySpanStyles() {
    const spanStyles = this.querySelectorAll('style[sn][block-id]');
    
    spanStyles.forEach(styleEl => {
      const sn = styleEl.getAttribute('sn');
      const blockId = styleEl.getAttribute('block-id');
      
      if (sn && blockId) {
        this.querySelectorAll(`.text-span[sn="${sn}"]`).forEach(targetSpan => {
          if (targetSpan && !targetSpan.classList.contains(blockId)) {
            targetSpan.classList.add(blockId)
          }
        })
      }
    });
  }
}
customElements.define('jxs-multi-span-text-component', JxsMultiSpanText);


/*
  功能：当容器内的元素获得 active 属性时，自动滚动使该元素居中显示

  使用方式：
  // 这些item如果被设置了active属性，那么这个item元素会被自动滚动到容器的中间展示。
  // 注意，一次只能有一个item为active。
  {% capture items %}
    <div>1</div>
    <div>2</div>
    <div>3</div>
    <div>4</div>
  {% endcapture %}
  // 如果需要，给一个容器来限制宽度
  <div class="container">
    {% render 'jxs-auto-scroll-layout', items: items %}
  </div>
  <style>
    .container {
      overflow: hidden;
    }
  </style>

  延迟初始化：如果items是其他组件动态创建的，可以手动初始化。
  {% render 'jxs-auto-scroll-layout' %}
  JS
  const autoScrollLayout = ...;
  autoScrollLayout.init(items);


  特性：
  - 水平滚动，隐藏滚动条（需配合CSS）
  - 监听子元素 active 属性变化
  - 平滑滚动到激活元素并居中显示
*/
class JxsAutoScrollLayout extends JxsComponent {
  constructor() {
    super();
    // MutationObserver 实例，用于监听属性变化
    this.observer = null;

    // 拖拽滚动状态
    this.isDragging = false;
    this.dragStartX = 0;
    this.dragStartScrollLeft = 0;
    this.dragPointerId = null;
    this.hasDragged = false;
    this.shouldPreventClick = false;
    this.dragThreshold = 5;
    this.pendingScrollToActive = false;

    this.handlePointerDown = this.handlePointerDown.bind(this);
    this.handlePointerMove = this.handlePointerMove.bind(this);
    this.handlePointerUp = this.handlePointerUp.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.handleDragStart = this.handleDragStart.bind(this);
  }

  init(items) {
    items.forEach(item => {
      this.refs.wrapper.append(item);
    });
    if (this.getDataset('autoScroll')) {
      this.initObserver();
      this.scrollToActive();
    }
    this.initDragScroll();
  }

  /**
   * 元素插入DOM时调用
   * 初始化监听器并立即滚动到当前激活的元素
   */
  connectedCallback() {
    super.connectedCallback();
    if (!this.hasAttribute('init-lazy')) {
      if (this.getDataset('autoScroll')) {
        this.initObserver();
        this.scrollToActive();
      }
      this.initDragScroll();
    }
  }

  /**
   * 元素从DOM移除时调用
   * 清理 MutationObserver，防止内存泄漏
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.observer) {
      this.observer.disconnect();
    }
    this.destroyDragScroll();
  }

  /**
   * 初始化 MutationObserver
   * 只监听 wrapper 直接子元素的 active 属性变化
   */
  initObserver() {
    if (this.observer) {
      this.observer.disconnect();
    }

    const wrapper = this.refs.wrapper;
    if (!(wrapper instanceof Element)) {
      console.warn('wrapper ref is missing');
      return;
    }

    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        // 当被观察元素的 active 属性发生变化时触发滚动
        if (mutation.type === 'attributes' && mutation.attributeName === 'active') {
          this.scrollToActive();
        }
      });
    });

    const observer = this.observer;

    Array.from(wrapper.children).forEach((element) => {
      observer.observe(element, {
        attributes: true,
        attributeFilter: ['active']
      });
    });
  }

  /**
   * 初始化拖拽滚动
   * 使用 Pointer Events 同时支持鼠标拖拽和触摸拖拽
   */
  initDragScroll() {
    this.addEventListener('pointerdown', this.handlePointerDown);
    window.addEventListener('click', this.handleClick, true);
    this.addEventListener('dragstart', this.handleDragStart);
  }

  /**
   * 销毁拖拽滚动监听器
   */
  destroyDragScroll() {
    this.removeEventListener('pointerdown', this.handlePointerDown);
    window.removeEventListener('pointermove', this.handlePointerMove);
    window.removeEventListener('pointerup', this.handlePointerUp);
    window.removeEventListener('pointercancel', this.handlePointerUp);
    window.removeEventListener('click', this.handleClick, true);
    this.removeEventListener('dragstart', this.handleDragStart);
  }

  /**
   * @param {PointerEvent} event
   */
  handlePointerDown(event) {
    // 鼠标只响应左键；触摸/触控笔无 button 时正常响应
    if (event.button !== undefined && event.button !== 0) {
      return;
    }

    // 没有可滚动内容时不进入拖拽状态，避免影响普通点击
    if (this.scrollWidth <= this.clientWidth) {
      return;
    }

    this.isDragging = true;
    this.hasDragged = false;
    this.shouldPreventClick = false;
    this.dragPointerId = event.pointerId;
    this.dragStartX = event.clientX;
    this.dragStartScrollLeft = this.scrollLeft;
    this.classList.add('is-dragging');

    // 不在 pointerdown 时捕获 pointer，避免普通点击被重定向到容器，导致子元素 click 失效。
    window.addEventListener('pointermove', this.handlePointerMove, { passive: false });
    window.addEventListener('pointerup', this.handlePointerUp);
    window.addEventListener('pointercancel', this.handlePointerUp);
  }

  /**
   * @param {PointerEvent} event
   */
  handlePointerMove(event) {
    if (!this.isDragging || event.pointerId !== this.dragPointerId) {
      return;
    }

    const deltaX = event.clientX - this.dragStartX;

    if (Math.abs(deltaX) > this.dragThreshold) {
      this.hasDragged = true;
      this.shouldPreventClick = true;
    }

    if (this.hasDragged) {
      event.preventDefault();
      this.scrollLeft = this.dragStartScrollLeft - deltaX;
    }
  }

  /**
   * @param {PointerEvent} event
   */
  handlePointerUp(event) {
    if (!this.isDragging || event.pointerId !== this.dragPointerId) {
      return;
    }

    window.removeEventListener('pointermove', this.handlePointerMove);
    window.removeEventListener('pointerup', this.handlePointerUp);
    window.removeEventListener('pointercancel', this.handlePointerUp);

    this.isDragging = false;
    this.dragPointerId = null;
    this.classList.remove('is-dragging');

    if (this.hasDragged) {
      window.setTimeout(() => {
        this.shouldPreventClick = false;
      }, 120);
    }

    this.hasDragged = false;

    if (this.pendingScrollToActive) {
      this.pendingScrollToActive = false;
      this.scrollToActive();
    }
  }

  /**
   * 拖拽超过阈值后阻止 click，避免拖动标签栏时误触 tab 切换
   * @param {MouseEvent} event
   */
  handleClick(event) {
    const path = event.composedPath?.() || [];

    if (!this.shouldPreventClick || !path.includes(this)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    this.shouldPreventClick = false;
  }

  /**
   * 阻止浏览器原生图片/链接拖拽，保证横向拖拽滚动稳定
   * @param {DragEvent} event
   */
  handleDragStart(event) {
    if (this.isDragging) {
      event.preventDefault();
    }
  }

  /**
   * 滚动到激活的元素并居中
   * 计算目标滚动位置，使激活元素在可视区域中间显示
   */
  scrollToActive() {
    if (this.isDragging) {
      this.pendingScrollToActive = true;
      return;
    }

    const wrapper = this.refs.wrapper;
    if (!(wrapper instanceof Element)) {
      return;
    }

    // 查找当前激活的 wrapper 直接子元素
    const activeElement = Array.from(wrapper.children).find((element) => element.hasAttribute('active'));
    if (activeElement) {
      const containerRect = this.getBoundingClientRect();
      const elementRect = activeElement.getBoundingClientRect();
      
      // 计算目标滚动位置：当前滚动位置 + 元素相对容器的偏移量 - 容器宽度的一半 + 元素宽度的一半
      // 这样可以让激活元素正好居中显示
      const scrollLeft = this.scrollLeft + (elementRect.left - containerRect.left) - (containerRect.width / 2) + (elementRect.width / 2);
      
      // 平滑滚动到目标位置
      this.scrollTo({
        left: scrollLeft,
        behavior: 'smooth'
      });
    }
  }
}
customElements.define('jxs-auto-scroll-layout', JxsAutoScrollLayout);

/**
 * JxsVideoSimulation - 轻量级“类 video”播放状态模拟组件。
 *
 * 该组件不渲染真实视频，只模拟 HTMLVideoElement 常用的播放属性和事件：
 * - currentTime / duration / paused / ended / playbackRate；
 * - play() / pause()；
 * - timeupdate / pause / ended 事件。
 *
 * 典型用途是在没有真实视频资源、但仍需要驱动进度条或轮播播放状态时，
 * 提供一个与 video 接近的状态源，便于复用依赖 video-like 对象的组件逻辑。
 */
class JxsVideoSimulation extends HTMLElement {
  constructor() {
    super();
    // 当前播放时间，内部统一使用毫秒存储；对外 getter/setter 使用秒，保持和 video API 一致。
    this._currentTime = 0;
    // 模拟总时长，单位毫秒；可通过 data-duration 配置，默认 5000ms。
    this._duration = parseInt(this.dataset.duration || '5000');
    // 播放速率，保留 video-like API 形态，当前默认固定为 1。
    this._playbackRate = 1;
    // 播放状态标记，true 表示正在通过 requestAnimationFrame 推进时间。
    this._isPlaying = false;
    // 结束状态标记，用于对外暴露 ended，并控制再次 play 时是否从头开始。
    this._ended = false;
    // 当前 requestAnimationFrame id，用于暂停或重启时取消旧帧。
    this._animationId = null;
    // 上一帧时间戳，用于计算两帧之间经过的时间。
    this._lastTime = 0;
    // 播放令牌，用于让旧的 rAF 回调失效，避免暂停/重播后旧回调继续推进。
    this._playToken = 0;
  }

  /**
   * 当前播放时间，单位秒。
   *
   * 对外保持 HTMLVideoElement.currentTime 的语义，内部再转换为毫秒计算。
   */
  get currentTime() {
    return this._currentTime / 1000;
  }

  /**
   * 手动设置当前播放时间，单位秒。
   *
   * 常用于外部 seek 或初始化进度；设置后会同步 ended 状态，并重置上一帧时间戳，
   * 避免下一次动画帧把 seek 前后的时间差错误叠加到播放进度中。
   */
  set currentTime(value) {
    const nextTime = Number(value) * 1000;

    this._currentTime = Number.isFinite(nextTime) ? Math.max(nextTime, 0) : 0;
    this._ended = this._duration > 0 && this._currentTime >= this._duration;
    // 重置时间戳避免计算跳跃
    this._lastTime = performance.now();
  }

  /**
   * 模拟总时长，单位秒。
   */
  get duration() {
    return this._duration / 1000;
  }

  /**
   * @param {number} value 单位毫秒
   */
  set duration(value) {
    const nextDuration = Number(value);

    this._duration = Number.isFinite(nextDuration) && nextDuration > 0 ? nextDuration : 0;

    if (this._duration > 0 && this._currentTime > this._duration) {
      this._currentTime = this._duration;
    }

    this._ended = this._duration > 0 && this._currentTime >= this._duration;
  }

  /**
   * 是否处于暂停状态。
   *
   * 与 HTMLVideoElement.paused 对齐：没有播放时返回 true。
   */
  get paused() {
    return !this._isPlaying;
  }

  /**
   * 是否已经播放到末尾。
   */
  get ended() {
    return this._ended;
  }

  /**
   * 当前播放倍率。
   */
  get playbackRate() {
    return this._playbackRate;
  }

  /**
   * 元素插入页面时执行。
   *
   * 自定义元素默认 display 可能是 inline，这里修正为 block，方便外部脚本绑定事件
   * 或在调试时观察元素占位。
   */
  connectedCallback() {
    // 确保元素可以接收事件监听
    if (!this.style.display || this.style.display === 'inline') {
      this.style.display = 'block';
    }
  }

  /**
   * 元素移除时暂停模拟播放，清理动画帧。
   */
  disconnectedCallback() {
    this.pause();
  }

  /**
   * 开始或恢复模拟播放。
   *
   * 如果已经结束，则从 0 重新开始；否则从当前 currentTime 继续。
   */
  play() {
    if (this._isPlaying) return;

    if (this._ended || (this._duration > 0 && this._currentTime >= this._duration)) {
      this._currentTime = 0;
      this._ended = false;
    }

    this._isPlaying = true;
    this._lastTime = performance.now();
    this._playToken += 1;
    this._queueAnimationFrame(this._playToken);
  }

  /**
   * 暂停模拟播放。
   *
   * 停止 rAF 推进并派发 pause/timeupdate，让依赖播放状态的组件可以立即同步 UI。
   */
  pause() {
    const wasPlaying = this._isPlaying;

    this._isPlaying = false;
    this._playToken += 1;

    if (this._animationId) {
      cancelAnimationFrame(this._animationId);
      this._animationId = null;
    }

    if (!wasPlaying) return;

    this.dispatchEvent(new CustomEvent('pause', {
      bubbles: true,
      cancelable: false
    }));

    // 立即同步一次 paused 状态，让依赖 timeupdate 的调用方停止补帧。
    this.dispatchEvent(new CustomEvent('timeupdate', {
      bubbles: true,
      cancelable: false
    }));
  }

  /**
   * 安排下一帧动画。
   *
   * @param {number} token 当前播放令牌，用于过滤过期回调。
   */
  _queueAnimationFrame(token) {
    if (this._animationId) {
      cancelAnimationFrame(this._animationId);
    }

    this._animationId = requestAnimationFrame(() => this._animate(token));
  }

  /**
   * rAF 主循环：根据上一帧到当前帧的时间差推进模拟播放时间。
   *
   * @param {number} token 当前播放令牌，用于确保只有最新一轮播放能更新状态。
   */
  _animate(token) {
    this._animationId = null;

    if (!this._isPlaying || token !== this._playToken) return;

    const now = performance.now();
    const delta = now - this._lastTime;
    this._lastTime = now;

    this._currentTime += delta * this._playbackRate;

    // 模拟结束
    if (this._duration > 0 && this._currentTime >= this._duration) {
      this._currentTime = this._duration;
      this._ended = true;
      this._isPlaying = false;
      this._playToken += 1;

      // 先发送最终 timeupdate，让进度条可以按 ended 状态渲染到 100%。
      this.dispatchEvent(new CustomEvent('timeupdate', {
        bubbles: true,
        cancelable: false
      }));

      this.dispatchEvent(new CustomEvent('ended', {
        bubbles: true,
        cancelable: false
      }));

      return;
    }

    // 发送 timeupdate 事件，模拟 video 的行为
    this.dispatchEvent(new CustomEvent('timeupdate', {
      bubbles: true,
      cancelable: false
    }));

    if (!this._isPlaying || token !== this._playToken) return;

    this._queueAnimationFrame(token);
  }
}
customElements.define('jxs-video-simulation', JxsVideoSimulation);


/**
 * 进度条默认 seek 判定阈值，单位秒。
 * 当前显示时间与新采样时间差距超过该值时，认为发生了跳转并立即吸附。
 */
const JXS_PROGRESS_BAR_SEEK_THRESHOLD = 1.5;
/**
 * 无新采样时允许继续补帧的最长时间，单位毫秒。
 * 超时后停止估算，避免进度条在外部播放源卡住或暂停时继续前进。
 */
const JXS_PROGRESS_BAR_STALL_TIMEOUT = 1200;
/**
 * 时间比较容差，单位秒。
 * 用于判断轻微误差和真实回退，避免浮点误差触发错误的 seek 判断。
 */
const JXS_PROGRESS_BAR_EPSILON = 0.02;

/**
 * JxsProgressBar - 基于外部采样数据的平滑进度条组件。
 *
 * 组件本身不绑定 video，也不主动监听 timeupdate；它只暴露 update 方法，
 * 由调用者在 timeupdate / ended / seek 等时机把当前播放状态传进来。
 *
 * 支持的调用方式：
 * - progressBar.update(videoLike)
 *   videoLike 只需要包含 currentTime、duration，可选 playbackRate、paused、ended。
 *   这个对象可以是真实 HTMLVideoElement，也可以是模拟播放器状态对象。
 *
 * - progressBar.update(percent)
 *   number 参数固定按百分比处理，范围为 0-100，适合 ended 时直接传入 100。
 *
 * 平滑策略：
 * - update(videoLike) 只记录最近一次 timeupdate 的时间采样点；
 * - 两次采样之间使用 requestAnimationFrame 根据 elapsed + playbackRate 补帧；
 * - 长时间收不到新采样时自动停止补帧，避免外部已暂停但进度条继续前进；
 * - 检测明显跳变或回退时立即吸附，保证 seek / loop 场景不会抖动。
 */
class JxsProgressBar extends JxsComponent {
  constructor() {
    super();

    // 最近一次外部传入的播放源状态。
    this._currentTime = 0;
    this._duration = 0;
    this._playbackRate = 1;
    this._paused = true;
    this._ended = false;

    // 采样与渲染状态：采样值用于估算，displayed* 用于记录当前实际显示值。
    this._sampleTime = 0;
    this._sampledAt = 0;
    this._displayedTime = 0;
    this._displayedPercent = 0;
    this._animationFrame = null;
    this._hasTimeSample = false;

    // 绑定方法上下文，确保作为回调传入 rAF 或外部事件时 this 仍指向组件实例。
    this.update = this.update.bind(this);
    this.render = this.render.bind(this);
  }

  /**
   * 元素挂载后初始化无障碍属性，并按当前缓存进度渲染一次。
   */
  connectedCallback() {
    super.connectedCallback();

    if (!this.hasAttribute('role')) {
      this.setAttribute('role', 'progressbar');
    }

    if (!this.hasAttribute('aria-valuemin')) {
      this.setAttribute('aria-valuemin', '0');
    }

    if (!this.hasAttribute('aria-valuemax')) {
      this.setAttribute('aria-valuemax', '100');
    }

    this.renderPercent(this._displayedPercent);
  }

  /**
   * 元素卸载时停止补帧，避免 rAF 回调持有已移除节点造成内存泄漏。
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    this.stopAnimation();
  }

  /**
   * seek 判定阈值，单位为秒。
   * 当新的采样时间和当前显示时间差距超过该阈值时，认为发生了 seek，
   * 进度条会立即吸附到新位置，而不是继续做平滑补帧。
   */
  get seekThreshold() {
    return JxsProgressBar.getFiniteNumber(this.dataset.seekThreshold, JXS_PROGRESS_BAR_SEEK_THRESHOLD);
  }

  /**
   * 无新 timeupdate 采样时的最大补帧时间，单位为毫秒。
   * 超过该时间仍没有收到新的 update(videoLike)，组件会停止 rAF，
   * 避免外部播放源已经暂停或卡住时进度条继续向前跑。
   */
  get stallTimeout() {
    return JxsProgressBar.getFiniteNumber(this.dataset.stallTimeout, JXS_PROGRESS_BAR_STALL_TIMEOUT);
  }

  /**
   * 对外暴露的进度同步入口。
   *
   * 调用者不需要把 video 绑定给组件，只需要在 timeupdate / pause / seek / ended 等事件中调用本方法，
   * 把当前播放状态传进来。组件会把这次输入当作一个“采样点”，并在两次采样之间使用 rAF 补帧。
   *
   * 支持两种输入：
   *
   * 1. number：百分比，范围 0-100。
   *    - update(0) 表示 0%；
   *    - update(100) 表示完成，常用于 ended；
   *    - 数字不会被当作秒数处理，避免 update(30) 同时可能代表 30 秒或 30%。
   *
   * 2. video-like 对象：真实 HTMLVideoElement 或结构相同的状态对象。
   *    - currentTime：当前播放时间，单位秒；
   *    - duration：总时长，单位秒；
   *    - playbackRate：播放倍率，可选，默认 1；
   *    - paused：是否暂停，可选，缺省按播放中处理；
   *    - ended：是否结束，可选，为 true 时直接渲染到 100%。
   *
   * @param {number | { currentTime?: number, duration?: number, playbackRate?: number, paused?: boolean, ended?: boolean, percent?: number }} input 百分比或 video-like 播放状态对象。
   */
  update(input) {
    if (typeof input === 'number') {
      this.updatePercent(input);
      return;
    }

    if (!input || typeof input !== 'object') return;

    this.updateBySource(input);
  }

  /**
   * 直接按百分比更新进度。
   *
   * number 参数固定表示百分比而不是秒数，避免 update(30) 同时可能代表
   * 30 秒或 30% 的歧义。
   *
   * @param {number} percent
   */
  updatePercent(percent) {
    const nextPercent = JxsProgressBar.clamp(Number(percent), 0, 100);

    this._displayedPercent = nextPercent;
    this._displayedTime = this._duration > 0 ? (this._duration * nextPercent) / 100 : 0;
    this._paused = true;
    this._ended = nextPercent >= 100;
    this._hasTimeSample = false;

    this.stopAnimation();
    this.renderPercent(nextPercent);
  }

  /**
   * 根据 video-like 对象记录一次播放进度采样。
   *
   * @param {{ currentTime?: number, duration?: number, playbackRate?: number, paused?: boolean, ended?: boolean, percent?: number }} source
   */
  updateBySource(source) {
    const currentTime = JxsProgressBar.getFiniteNumber(source.currentTime);
    const duration = JxsProgressBar.getFiniteNumber(source.duration);

    if (!Number.isFinite(currentTime) || !Number.isFinite(duration) || duration <= 0) {
      if (Number.isFinite(Number(source.percent))) {
        this.updatePercent(Number(source.percent));
      }

      return;
    }

    const now = this.getNow();
    const playbackRate = JxsProgressBar.getFiniteNumber(source.playbackRate, 1);
    const paused = source.paused === undefined ? false : Boolean(source.paused);
    const ended = Boolean(source.ended);
    const isFirstSample = !this._hasTimeSample;
    const isBackwardSeek = !isFirstSample && currentTime + JXS_PROGRESS_BAR_EPSILON < this._currentTime;
    const isLargeJump = !isFirstSample && Math.abs(currentTime - this._displayedTime) > this.seekThreshold;
    const shouldSnap = isFirstSample || isBackwardSeek || isLargeJump || paused || ended;

    this._currentTime = currentTime;
    this._duration = duration;
    this._playbackRate = playbackRate;
    this._paused = paused || ended;
    this._ended = ended;
    this._sampleTime = currentTime;
    this._sampledAt = now;
    this._hasTimeSample = true;

    if (ended) {
      this._displayedTime = duration;
      this.stopAnimation();
      this.updateProgressUI();
      return;
    }

    if (shouldSnap) {
      this._displayedTime = JxsProgressBar.clamp(currentTime, 0, duration);
    } else if (currentTime > this._displayedTime) {
      this._displayedTime = currentTime;
    }

    this.updateProgressUI();

    if (this._paused) {
      this.stopAnimation();
      return;
    }

    this.startAnimation();
  }

  /**
   * 启动基于 requestAnimationFrame 的补帧渲染。
   */
  startAnimation() {
    if (this._animationFrame) return;

    this._animationFrame = window.requestAnimationFrame(this.render);
  }

  /**
   * 停止补帧渲染。
   */
  stopAnimation() {
    if (!this._animationFrame) return;

    window.cancelAnimationFrame(this._animationFrame);
    this._animationFrame = null;
  }

  /**
   * 在两次 timeupdate 采样之间估算当前进度，并更新 UI。
   */
  render() {
    this._animationFrame = null;

    if (!this._hasTimeSample || this._paused || this._ended || this._duration <= 0) {
      this.updateProgressUI();
      return;
    }

    const elapsedMs = this.getNow() - this._sampledAt;
    const shouldEstimate = elapsedMs <= this.stallTimeout;
    let estimatedTime = this._sampleTime;

    if (shouldEstimate) {
      estimatedTime = this._sampleTime + (elapsedMs / 1000) * this._playbackRate;
    }

    estimatedTime = JxsProgressBar.clamp(estimatedTime, 0, this._duration);

    if (this._playbackRate >= 0) {
      this._displayedTime = Math.max(this._displayedTime, estimatedTime);
    } else {
      this._displayedTime = estimatedTime;
    }

    this.updateProgressUI();

    if (shouldEstimate && this._displayedTime < this._duration) {
      this._animationFrame = window.requestAnimationFrame(this.render);
    }
  }

  /**
   * 根据当前显示时间计算百分比并写入 UI。
   */
  updateProgressUI() {
    if (this._duration <= 0) {
      this.renderPercent(this._displayedPercent);
      return;
    }

    const percent = JxsProgressBar.clamp((this._displayedTime / this._duration) * 100, 0, 100);

    this.renderPercent(percent);
  }

  /**
   * 将百分比写入 CSS 变量和无障碍属性。
   *
   * @param {number} percent
   */
  renderPercent(percent) {
    const nextPercent = JxsProgressBar.clamp(Number(percent), 0, 100);
    const progressScale = nextPercent / 100;

    this._displayedPercent = nextPercent;
    this.style.setProperty('--jxs-progress-bar-percent', `${nextPercent}%`);
    this.style.setProperty('--jxs-progress-bar-scale', String(progressScale));
    this.setAttribute('aria-valuenow', String(Math.round(nextPercent)));
    this.toggleAttribute('progress-empty', nextPercent <= 0);
    this.toggleAttribute('progress-complete', nextPercent >= 100);
  }

  /**
   * 获取高精度时间戳。
   */
  getNow() {
    return window.performance?.now ? window.performance.now() : Date.now();
  }

  /**
   * @param {unknown} value
   * @param {number} [fallback]
   */
  static getFiniteNumber(value, fallback = NaN) {
    const number = Number(value);

    return Number.isFinite(number) ? number : fallback;
  }

  /**
   * @param {number} value
   * @param {number} min
   * @param {number} max
   */
  static clamp(value, min, max) {
    if (Number.isNaN(value)) return min;

    return Math.min(Math.max(value, min), max);
  }
}
customElements.define('jxs-progress-bar', JxsProgressBar);

class JxsDialog extends JxsComponent {
  constructor() {
    super();
  }

  #shopifyBlockSelect = event => {
    if (this === event.target || this.contains(event.target)) {
      this.setAttribute('open', '');
    }
  }

  #shopifyBlockDeselect = event => {
    if (this === event.target || this.contains(event.target)) {
      this.removeAttribute('open');
    }
  }

  connectedCallback() {
    super.connectedCallback();
    if (Shopify.designMode) {
      document.addEventListener('shopify:block:load', this.#shopifyBlockSelect);
      document.addEventListener('shopify:block:select', this.#shopifyBlockSelect);
      document.addEventListener('shopify:block:deselect', this.#shopifyBlockDeselect);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (Shopify.designMode) {
      document.removeEventListener('shopify:block:load', this.#shopifyBlockSelect);
      document.removeEventListener('shopify:block:select', this.#shopifyBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.#shopifyBlockDeselect);
    }
  }

  open() {
    this.setAttribute('open', '');
    this.dispatchEvent(new CustomEvent('jxs:dialog:open', {
      bubbles: true,
      detail: {
        dialog: this
      }
    }));
  }

  close() {
    this.removeAttribute('open');
    this.dispatchEvent(new CustomEvent('jxs:dialog:close', {
      bubbles: true,
      detail: {
        dialog: this
      }
    }));
  }

  updateContent(content) {
    this.refs.content.innerHTML = '';
    if (content) {
      if (typeof content == 'string') {
        this.refs.content.innerHTML = content;
      } else {
        this.refs.content.append(content);
      }
    }
  }

  updateTitle(title) {
    this.refs.title.textContent = title;
  }
}
customElements.define('jxs-dialog', JxsDialog);

class JxsCartDrawerPromotionProducts extends JxsComponent {

  #swiper = null;

  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    this.#initSwiper();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#swiper?.destroy(true, true);
  }

  #initSwiper() {
    this.#swiper = new Swiper(this.refs.swiper, {
      loop: false,
      autoplay: false,
      rewind: false,
      slidesPerView: 1.4,
      direction: 'horizontal',
      spaceBetween: 14,
      breakpoints: {
        768: {
          direction: 'vertical',
          slidesPerView: 'auto'
        }
      }
    });
  }
}
customElements.define('jxs-cart-drawer-promotion-products', JxsCartDrawerPromotionProducts);

class JxsQuantityInput extends JxsComponent {
  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  updateQuantity(data) {
    let q = 1
    try {
      q = parseInt(this.refs.quantity.value);
    } catch (e) {}
    if (isNaN(q)) {
      q = 1;
    }
    q = q + data.val;
    if (q <= 0) {
      q = 1;
    }
    this.refs.quantity.value = q;
  }

  getQuantity() {
    return parseInt(this.refs.quantity.value) || 1;
  }
}
customElements.define('jxs-quantity-input', JxsQuantityInput);

class JxsAlsoBuyProduct extends JxsComponent {
  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  handleAddToCart() {
    this.refs.button.setAttribute('aria-disabled', true);
    this.refs.button.classList.add('loading');
    this.refs.button.querySelector('.loading__spinner').classList.remove('hidden');
    this.refs.error.textContent = '';

    const quantity = this.refs.quantity_input.getQuantity();
    const variantId = this.refs.variant.selectedOptions[0]?.value;

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', quantity ?? 1);

    const cart = document.querySelector('cart-drawer');
    if (cart) {
      formData.append('sections', cart.getSectionsToRender().map((section) => section.id));
      formData.append('sections_url', window.location.pathname);
      cart.setActiveElement(document.activeElement);
    }

    config.body = formData;

    fetch(`${routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.refs.error.textContent = response.description
          return;
        } else if (!cart) {
          window.location = `${window.routes?.cart_url}`;
          return;
        }
        cart.renderContents(response);
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        this.refs.button.classList.remove('loading');
        this.refs.button.removeAttribute('aria-disabled');
        this.refs.button.querySelector('.loading__spinner').classList.add('hidden');
      });
  }
}
customElements.define('jxs-also-buy-product', JxsAlsoBuyProduct);


class JxsShippingMessage extends JxsComponent {
  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    const dayFast = this.getDataset('dayFast');
    const daySlow = this.getDataset('daySlow');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    
    const formatDate = (date) => {
      const dayName = days[date.getDay()];
      const monthName = months[date.getMonth()];
      const dayNum = date.getDate();
      return `${dayName}, ${monthName} ${dayNum}`;
    };
    
    const today = new Date();
    const startDate = new Date(today);
    startDate.setDate(today.getDate() + dayFast);
    const endDate = new Date(today);
    endDate.setDate(today.getDate() + daySlow);
    
    const dateRange = `${formatDate(startDate)} - ${formatDate(endDate)}`;

    const replacements = [
      ['_date_', dateRange],
      ['_country_', this.dataset.country]
    ]
    replaceNodeText(this.querySelectorAll('.jxs-block-text'), replacements);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }
}
customElements.define('jxs-shipping-message', JxsShippingMessage);

/**
 * 展示产品的售价，原价，折扣。
 * 监听variant变化，更新金额信息。
 */
class JxsProductPrice extends JxsComponent {
  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    try {
      if (typeof MainEvents !== 'undefined' && MainEvents.variantUpdate) {
        document.addEventListener(MainEvents.variantUpdate, this.updatePrice);
      }
    } catch (e) {
      // silent
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    try {
      if (typeof MainEvents !== 'undefined' && MainEvents.variantUpdate) {
        document.removeEventListener(MainEvents.variantUpdate, this.updatePrice);
      }
    } catch (e) {
      // silent
    }
  }

  updatePrice = (event) => {
    try {
      if (event?.detail?.data?.newProduct) {
        this.dataset.productId = event.detail.data.newProduct.id;
      } else if (event?.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
        return;
      }

      const variant = event?.detail?.resource;
      if (variant) {
        this.refs.priceSale.textContent = formatMoney(variant.price);
        this.refs.priceCompare.classList.remove('hidden');
        this.refs.priceSave.classList.remove('hidden');
        if (variant.compare_at_price > variant.price) {
          this.refs.priceCompare.textContent = formatMoney(variant.compare_at_price);
          const savePriceValue = variant.compare_at_price - variant.price;
          if (this.refs.priceSave.getAttribute('type') == 'price') {
            this.refs.priceSave.textContent = '-' + formatMoney(savePriceValue);
          } else {
            const savePercent = (savePriceValue * 100.0 / variant.compare_at_price).toFixed(0);
            this.refs.priceSave.textContent = '-' + savePercent + '%';
          }
        } else {
          this.refs.priceCompare.classList.add('hidden');
          this.refs.priceSave.classList.add('hidden');
        }
      }
    } catch (e) {
      // silent
    }
  };
}
customElements.define('jxs-product-price', JxsProductPrice);

/*
  展示产品的折后价。
  展示：
  利用frontstore api创建一个临时的购物车对象，将折扣码和产品变体放进去，
  拿到折扣码的折后价，然后展示出来。

  复制：
  点击复制折扣码，可将折扣码复制到剪贴板，并且将折扣码应用到主购物车中。
*/
class JxsProductDiscount extends JxsComponent {
  constructor() {
    super();
    this.discountCode = this.getDataset('code')
  }

  connectedCallback() {
    super.connectedCallback();
    try {
      if (typeof MainEvents !== 'undefined' && MainEvents.variantUpdate) {
        document.addEventListener(MainEvents.variantUpdate, this.handleVariantChanged);
      }
    } catch (e) {
      // silent
    }
    this.showDiscountPrice(this.getDataset('variantId'));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    try {
      if (typeof MainEvents !== 'undefined' && MainEvents.variantUpdate) {
        document.removeEventListener(MainEvents.variantUpdate, this.handleVariantChanged);
      }
    } catch (e) {
      // silent
    }
  }

  handleVariantChanged = (event) => {
    try {
      if (event?.detail?.data?.newProduct) {
        this.dataset.productId = event.detail.data.newProduct.id;
      } else if (event?.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
        return;
      }

      const variant = event?.detail?.resource;
      this.showDiscountPrice(variant.id);
    } catch (e) {
      // silent
    }
  };

  async showDiscountPrice(variantId) {
    const code = this.discountCode;
    const shopDomain = this.getDataset('shopDomain') || window.Shopify.shop;

    // 基础校验
    if (!variantId || !code) {
      console.warn('[JxsProductDiscount] 缺少 variantId 或 discount code');
      return;
    }

    try {
      const cart = await this.createCartWithDiscount(variantId, code, shopDomain);
      if (!cart.discountCodes[0].applicable) {
        throw new Error('折扣应用失败，可能和其他折扣互斥，请检查')
      }
      const amount = parseFloat(cart.lines.edges[0].node.discountAllocations[0].discountedAmount.amount);
      const discountPrice = formatMoney(amount * 100);
      const textNode = document.importNode(this.refs.tmpl.content, true);
      const replacements = [
        ['_discount_', discountPrice], 
        ['_code_', code]
      ];
      replaceNodeText(textNode, replacements)
      this.refs.info.innerHTML = '';
      this.refs.info.append(textNode);
      this.setAttribute('enabled', '');
    } catch (err) {
      if (Shopify.designMode) {
        this.setAttribute('enabled', '');
      } else {
        this.removeAttribute('enabled');
      }
      this.refs.info.innerHTML = '此信息只在模板编辑模式下可看：' + err.message;
      console.error('[JxsProductDiscount] 调用 Storefront API 失败:', err);
    }
  }

  /**
   * 调用 Storefront API 创建带折扣的购物车
   */
  async createCartWithDiscount(variantId, discountCode, shopDomain) {
    variantId = 'gid://shopify/ProductVariant/' + variantId;
    const endpoint = `https://${shopDomain}/api/2026-01/graphql.json`;

    const query = `
      mutation cartCreate($input: CartInput!) {
        cartCreate(input: $input) {
          cart {
            lines(first: 1) {
              edges {
                node {
                  merchandise {
                    ... on ProductVariant {
                      title
                      price {
                        amount
                        currencyCode
                      }
                    }
                  }
                  discountAllocations {
                    discountedAmount {
                      amount
                      currencyCode
                    }
                  }
                }
              }
            }
            discountCodes {
              code
              applicable
            }
          }
          userErrors {
            field
            message
          }
        }
      }
    `;

    const variables = {
      input: {
        lines: [
          {
            merchandiseId: variantId,
            quantity: 1
          }
        ],
        discountCodes: [discountCode]
      }
    };

    const res = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ query, variables })
    });

    const data = await res.json();

    // 处理 userErrors
    if (data.data?.cartCreate?.userErrors?.length) {
      throw new Error(data.data.cartCreate.userErrors.map(e => e.message).join(', '));
    }

    if (!data.data?.cartCreate?.cart) {
      throw new Error('创建购物车失败');
    }

    return data.data.cartCreate.cart;
  }

  async copyCode(data) {
    if (data.apply) {
      this.refs.button.setAttribute('disabled', '');
      this.refs.button.setAttribute('loading', '');
      try {
        await this.applyDiscountCode();
      } catch (e) {
      }
      this.refs.button.removeAttribute('disabled');
      this.refs.button.removeAttribute('loading');
    }
    const copySuccess = await copyToClipboard(this.discountCode);
    if (copySuccess) {
      this.refs.buttonText.textContent = this.refs.button.dataset.buttonTextCopied;
      clearTimeout(this._clearButtonTextId);
      this._clearButtonTextId = setTimeout(() => {
        this.refs.buttonText.textContent = this.refs.button.dataset.buttonText;
      }, 2000)
    }
  }

  async applyDiscountCode() {
    const cartCodes = await this.getCartDiscountCodes();
    if (cartCodes.includes(this.discountCode)) {
      return
    }
    cartCodes.push(this.discountCode);
    const cart = document.querySelector('cart-drawer');
    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        discount: cartCodes.join(','),
        sections: cart.getSectionsToRender().map((section) => section.id),
        sections_url: window.location.pathname
      })
    });
    const respData = await response.json();
    cart.renderContents(respData);
  }

  async getCartDiscountCodes() {
    const response = await fetch('/cart.js', {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    });
    const cart = await response.json();
    const discountCodes = cart.discount_codes.map(item => item.code);
    return discountCodes;
  }
}

customElements.define('jxs-product-discount', JxsProductDiscount);

class JxsRichProductCard extends JxsComponent {
  connectedCallback() {
    super.connectedCallback();

    if (this.dataset.jxsCheckoutBound === 'true') return;
    if (!this.dataset.productTitle) return;

    this.dataset.jxsCheckoutBound = 'true';
    this.#bindCheckout();
  }

  #normalize(value) {
    return String(value || '').replace(/\s+/g, ' ').trim();
  }

  #variants() {
    const jsonEl = this.querySelector('script[data-product-variants]');
    if (!jsonEl) return [];
    try {
      const data = JSON.parse(jsonEl.textContent);
      return Array.isArray(data) ? data : [];
    } catch (e) {
      return [];
    }
  }

  #getSelectedOptionValues(picker) {
    const values = [];
    picker.querySelectorAll('.product-form__input').forEach((wrapper) => {
      const select = wrapper.querySelector('select');
      if (select && select.value) {
        values.push(select.value);
        return;
      }
      const checked = wrapper.querySelector('input:checked');
      if (checked && checked.value) values.push(checked.value);
    });
    return values;
  }

  #matchVariantId(picker, variants) {
    const selectedValues = this.#getSelectedOptionValues(picker);
    if (!selectedValues.length || !variants.length) return '';

    const match = variants.find((variant) => {
      const options = Array.isArray(variant.options)
        ? variant.options
        : [variant.option1, variant.option2, variant.option3].filter((value) => value != null);
      return options.length === selectedValues.length && options.every((option, index) => option === selectedValues[index]);
    });

    return match && match.id ? String(match.id) : '';
  }

  #parseFallbackVariantId(root) {
    if (!root) return '';
    const idInput = root.querySelector('input.product-variant-id, input[name="id"]');
    if (idInput && idInput.value) return String(idInput.value);
    const jsonEl = root.querySelector('[data-selected-variant]');
    if (jsonEl && jsonEl !== this.querySelector('script[data-product-variants]')) {
      try {
        const data = JSON.parse(jsonEl.textContent);
        if (data && data.id) return String(data.id);
      } catch (e) {}
    }
    return '';
  }

  #findNearbyPicker() {
    let node = this;
    while (node && node !== document.documentElement) {
      const picker = node.querySelector('variant-selects');
      if (picker) return picker;
      if (node !== this && node.matches && (node.matches('product-info') || node.matches('.shopify-section'))) {
        break;
      }
      node = node.parentElement;
    }
    return null;
  }

  #findNearbyVariantId(variants) {
    const picker = this.#findNearbyPicker();
    if (picker) {
      const matchedId = this.#matchVariantId(picker, variants);
      if (matchedId) return matchedId;
      const fallbackId = this.#parseFallbackVariantId(picker);
      if (fallbackId) return fallbackId;
    }
    return this.dataset.variantId || '';
  }

  #checkoutHref(variants) {
    const variantId = this.#findNearbyVariantId(variants);
    if (!variantId) return '';
    const cartUrl = (this.dataset.cartUrl || '/cart').replace(/\/$/, '');
    const href = cartUrl + '/' + variantId + ':1';
    this.dataset.variantId = variantId;
    return href;
  }

  #bindCheckout() {
    const productTitle = this.#normalize(this.dataset.productTitle);
    const variants = this.#variants();
    const mediaLink = this.querySelector('a.rich-product-card__media');
    const targets = [];

    if (mediaLink) targets.push(mediaLink);

    this.querySelectorAll('.rich-product-card__content .jxs-block-text').forEach((node) => {
      if (this.#normalize(node.textContent) !== productTitle) return;
      node.classList.add('is-checkout-link');
      targets.push(node);
    });

    if (!targets.length) return;

    const applyCheckoutUrl = () => {
      const href = this.#checkoutHref(variants);
      if (href && mediaLink) mediaLink.setAttribute('href', href);
      return href;
    };

    applyCheckoutUrl();

    const scope = this.closest('product-info') || this.closest('.shopify-section') || this;
    scope.addEventListener('change', (event) => {
      if (!event.target.closest('variant-selects')) return;
      applyCheckoutUrl();
    });

    targets.forEach((target) => {
      target.addEventListener('click', (event) => {
        const href = applyCheckoutUrl();
        if (!href) return;
        event.preventDefault();
        window.location.href = href;
      });
    });
  }
}

customElements.define('jxs-rich-product-card', JxsRichProductCard);